import { Icon } from '@buggy/shared'
import { Flex, Skeleton, Spin } from 'antd'
import dayjs from 'dayjs'
import { isNil } from 'lodash-es'
import isNumber from 'lodash-es/isNumber'
import sortBy from 'lodash-es/sortBy'
import uniqBy from 'lodash-es/uniqBy'
import { FunctionComponent, ReactNode, useMemo } from 'react'
import { useWindowSize } from 'react-use'
import styled, {
  css,
  FlattenSimpleInterpolation,
  useTheme,
} from 'styled-components'
import { ApexChartOptions, ApexCharts } from '@/components/common/apex-charts'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import FansUpRateStatistic from '@/components/kol/common/statistics/fans-up-rate-statistic'
import Blocker from '@/components/kol/detail/blocker'
import { NoData } from '@/components/kol/detail/no-data'
import useFollowerAnalyze from '@/components/kol/hooks/use-follower-analyze'
import { mockKolInfoKpi } from '@/constants/kol-detail/info-kpi'
import { mockFixedTrackingAnalysisData } from '@/constants/kol-detail/mock-data'
import useFormatNumberRange from '@/hooks/intl/use-format-number-range'
import {
  DURATION_MONTH_INTEGER_MAP,
  KolDetailDuration,
} from '@/hooks/use-authorization/routes'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Follower } from '@/store/kol-detail'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'
import inTimeBoundDate from '@/utils/detail/in-time-bound-date'
import {
  getPotentialI18n,
  Potential,
  POTENTIAL_STYLED_MAP,
} from '@/utils/detail/potential'
import { getBoundaryDateSet } from '@/utils/format-date'

type SeriesItem = {
  x: string
  y: number | null
}

interface FollowerAnalysisProps {
  kolInfo?: KolSchemaModel
  isFetchingKolInfo: boolean
  platform: Platform
  duration: KolDetailDuration
  isKolInfoLocked: boolean
  showMockData?: boolean
}

const mockKolInfo = {
  ...mockKolInfoKpi,
  minEstReachCountCol1: 38000,
  maxEstReachCountCol1: 45000,
  minEstReachCountCol2: 3489000,
  maxEstReachCountCol2: 110000000,
}

const FollowerAnalysis: FunctionComponent<FollowerAnalysisProps> = ({
  kolInfo,
  platform,
  duration,
  isFetchingKolInfo,
  showMockData,
  isKolInfoLocked,
}) => {
  const { formatMessage, formatNumber, formatDate } = useIntl()
  const formatNumberRange = useFormatNumberRange()
  const { width } = useWindowSize()
  const theme = useTheme()

  const isPublicKolDetailPage = useIsPublicDetailPage()
  const { followerAnalyze, isFetching } = useFollowerAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dcIdHashed,
    isKolInfoLocked,
    isFetchingKolInfo,
  })

  const widerLayoutMode = [
    PlatformShortcode.TikTok,
    PlatformShortcode.Twitter,
  ].includes(platform.shortcode)
  const hiddenData = isKolInfoLocked && !showMockData

  const kpi = useMemo(
    () => kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode),
    [kolInfo, platform],
  )

  const [fansUpCount, potential] = ((): [
    number | undefined,
    Potential | undefined,
  ] => {
    if (isKolInfoLocked) {
      return [mockKolInfo.fansUpRateCount3m, mockKolInfo.potential]
    }

    const fansUpCount =
      duration === Duration.ThreeMonths
        ? kpi?.fansUpRateCount3m
        : kpi?.fansUpRateCount6m

    const potential = ((): Potential | undefined => {
      if (!kpi?.potential || kpi.potential === Potential.Any) {
        return
      }

      if (
        ![
          PlatformShortcode.Instagram,
          PlatformShortcode.YouTube,
          PlatformShortcode.Facebook,
        ].includes(platform.shortcode)
      ) {
        return
      }

      return kpi.potential
    })()

    return [fansUpCount, potential]
  })()

  const reachCountEstList = useMemo((): {
    titleI18nId: I18nId
    tooltipI18nId: I18nId
    formattedRange: string | null
  }[] => {
    const mapEstReachRange = (
      min?: number | null,
      max?: number | null,
    ): string | null => {
      if (isNumber(min) && isNumber(max)) {
        return formatNumberRange(min, max, {
          isCompactDisplay: true,
          separator: '-',
        })
      }
      return null
    }

    const [estImageReach, estVideoReach, estShortReach] =
      duration === Duration.ThreeMonths
        ? [
            mapEstReachRange(
              kpi?.minImageEstimatedReachCount3m,
              kpi?.maxImageEstimatedReachCount3m,
            ),
            mapEstReachRange(
              kpi?.minVideoEstimatedReachCount3m,
              kpi?.maxVideoEstimatedReachCount3m,
            ),
            mapEstReachRange(
              kpi?.minShortEstimatedReachCount3m,
              kpi?.maxShortEstimatedReachCount3m,
            ),
          ]
        : [
            mapEstReachRange(
              kpi?.minImageEstimatedReachCount6m,
              kpi?.maxImageEstimatedReachCount6m,
            ),
            mapEstReachRange(
              kpi?.minVideoEstimatedReachCount6m,
              kpi?.maxVideoEstimatedReachCount6m,
            ),
            mapEstReachRange(
              kpi?.minShortEstimatedReachCount6m,
              kpi?.maxShortEstimatedReachCount6m,
            ),
          ]

    const mapFormattedRange = (
      col: 'estReachCountCol1' | 'estReachCountCol2',
      range: string | null,
    ): string | null => {
      if (isKolInfoLocked) {
        return col === 'estReachCountCol1'
          ? mapEstReachRange(
              mockKolInfo.minEstReachCountCol1,
              mockKolInfo.maxEstReachCountCol1,
            )
          : mapEstReachRange(
              mockKolInfo.minEstReachCountCol2,
              mockKolInfo.maxEstReachCountCol2,
            )
      }

      return range
    }

    switch (platform.shortcode) {
      case PlatformShortcode.Instagram:
        return [
          {
            titleI18nId: 'kol:post_estimated_reach',
            tooltipI18nId: 'kol:post_estimated_reach_tooltip',
            formattedRange: mapFormattedRange(
              'estReachCountCol1',
              estImageReach,
            ),
          },
          {
            titleI18nId: 'kol:video_estimated_reach',
            tooltipI18nId: 'kol:video_estimated_reach_tooltip',
            formattedRange: mapFormattedRange(
              'estReachCountCol2',
              estShortReach,
            ),
          },
        ]
      case PlatformShortcode.Facebook:
        return [
          {
            titleI18nId: 'kol:post_estimated_reach',
            tooltipI18nId: 'kol:post_estimated_reach_tooltip',
            formattedRange: mapFormattedRange(
              'estReachCountCol1',
              estImageReach,
            ),
          },
          {
            titleI18nId: 'kol:video_estimated_reach',
            tooltipI18nId: 'kol:video_estimated_reach_tooltip',
            formattedRange: mapFormattedRange(
              'estReachCountCol2',
              estVideoReach,
            ),
          },
        ]
      case PlatformShortcode.YouTube:
        return [
          {
            titleI18nId: 'kol:long_video_estimated_reach',
            tooltipI18nId: 'kol:long_video_estimated_reach_tooltip',
            formattedRange: mapFormattedRange(
              'estReachCountCol1',
              estVideoReach,
            ),
          },
          {
            titleI18nId: 'kol:short_video_estimated_reach',
            tooltipI18nId: 'kol:short_video_estimated_reach_tooltip',
            formattedRange: mapFormattedRange(
              'estReachCountCol2',
              estShortReach,
            ),
          },
        ]
      default:
        return []
    }
  }, [duration, formatNumber, isKolInfoLocked, kpi, platform.shortcode])

  const [series, chartOptions] = useMemo(() => {
    const followerList = ((): Follower[] => {
      if (isKolInfoLocked) {
        return mockFixedTrackingAnalysisData
      }

      const followers = followerAnalyze?.[platform.code] ?? []
      return uniqBy(
        inTimeBoundDate(
          followers,
          DURATION_MONTH_INTEGER_MAP[duration],
          'createdAt',
        ),
        (follower) => formatDate(follower.createdAt),
      )
    })()

    const sortedSeries = sortBy(
      followerList.map(
        (follower): SeriesItem => ({
          x: formatDate(follower.createdAt),
          y: follower.follower,
        }),
      ),
      (item) => dayjs(item.x),
    )

    const tickAmount = 18

    // 算出依照 tickAmount x 軸實際會顯示的日期，再去計算出 boundary date
    // x 軸顯示的日期數量為 tickAmount + 1，當 concatSeries 數量小於 tickAmount + 1 時，則全部顯示
    const boundaryDates = ((): Set<string> => {
      const tickDates: string[] = []
      const addDaysRange =
        Math.round(sortedSeries.length / (tickAmount + 1)) || 1

      for (let i = 0; i < sortedSeries.length; i += addDaysRange) {
        tickDates.push(sortedSeries[i].x)
      }

      return getBoundaryDateSet(tickDates)
    })()

    const digitLength = fansUpCount
      ? Math.abs(Math.floor(fansUpCount)).toString().length
      : 3

    const chartOptions: ApexChartOptions = {
      chart: {
        id: 'follower-analysis-chart',
        toolbar: {
          show: false,
        },
        height: width > 980 ? 270 : 150,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: sortedSeries.length === 1 ? 3 : 0, // 把 marker 藏起來, hover 依然可以顯示，但只有一個點的時候需要顯示
        showNullDataPoints: false,
        colors: theme.colors.kolrBrand.primary,
        strokeColors: theme.colors.kolrBrand.primary,
      },
      stroke: {
        curve: 'smooth',
        colors: [theme.colors.kolrBrand.primary],
      },
      yaxis: {
        opposite: true,
        tickAmount: 4,
        minOptions: {
          method: 'floor',
          digitLength,
        },
        maxOptions: {
          digitLength,
        },
        labels: {
          formatter: (val: number): string => {
            return formatNumber(val)
          },
        },
      },
      xaxis: {
        offsetX: 8,
        crosshairs: {
          show: false,
        },
        tooltip: {
          enabled: false,
        },
        tickAmount,
        labels: {
          rotateAlways: true,
          formatter: (val: string): string => {
            return boundaryDates.has(val)
              ? formatDate(val)
              : formatDate(val, 'numericMonthDay')
          },
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          colorStops: [
            {
              offset: 0,
              color: theme.colors.kolrBrand.primary,
              opacity: 0.4,
            },
            {
              offset: 40,
              color: theme.colors.kolrBrand.primary,
              opacity: 0.2,
            },
            {
              offset: 100,
              color: theme.colors.kolrBrand.primary,
              opacity: 0,
            },
          ],
        },
      },
      tooltip: {
        enabled: true,
        shared: false,
        marker: {
          fillColors: [theme.colors.kolrBrand.primary],
        },
      },
    }

    return [sortedSeries, chartOptions, theme.colors.kolrBrand.primary]
  }, [
    duration,
    fansUpCount,
    followerAnalyze,
    formatDate,
    formatNumber,
    isKolInfoLocked,
    platform.code,
    theme.colors.kolrBrand.primary,
    width,
  ])

  return (
    <KolDetailDataBlock
      colSpan={widerLayoutMode ? 24 : 12}
      header={{
        title: `${platform.title} ${formatMessage({
          id: 'kol:follower_analysis_title',
        })}`,
      }}
      kolId={kolInfo?.id}
      kolMetrics='Followers'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Wrapper $widerLayoutMode={widerLayoutMode}>
        {isFetchingKolInfo ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : (
          <StatisticsWrapper style={{ width: '100%' }}>
            <>
              <StyledKolDetailStatistic
                title={formatMessage({ id: 'campaign:followers_text' })}
                value={
                  isNil(kpi?.follower) || kpi.follower === 0
                    ? null
                    : formatNumber(kpi.follower)
                }
              />
              <StyledKolDetailStatistic
                title={formatMessage({ id: 'kol:platform_data_fans_up' })}
                tooltip={{
                  title: formatMessage(
                    { id: 'kol:platform_data_fans_up_tooltip' },
                    { n: DURATION_MONTH_INTEGER_MAP[duration] },
                  ),
                }}
                value={
                  isNil(fansUpCount) || fansUpCount === 0 ? null : (
                    <Blur activate={hiddenData}>
                      {formatNumber(fansUpCount)}
                    </Blur>
                  )
                }
              />
              <FansUpRateStatistic
                duration={duration}
                isKolInfoLocked={isKolInfoLocked}
                kpi={kpi}
                loading={isFetching}
                showMockData={showMockData}
              />
            </>
            {kpi &&
              [
                PlatformShortcode.Instagram,
                PlatformShortcode.YouTube,
                PlatformShortcode.Facebook,
              ].includes(platform.shortcode) && (
                <>
                  <StyledKolDetailStatistic
                    prefix={
                      potential ? (
                        <Flex align='center'>
                          <Blur activate={hiddenData}>
                            <Icon
                              color={POTENTIAL_STYLED_MAP[potential].color}
                              fontSize={20}
                              name={POTENTIAL_STYLED_MAP[potential].iconName}
                            />
                          </Blur>
                        </Flex>
                      ) : undefined
                    }
                    title={formatMessage({ id: 'kol:platform_data_potential' })}
                    tooltip={{
                      title: formatMessage({
                        id: 'kol:kol_potential_detail_tooltip',
                      }),
                    }}
                    value={
                      potential ? (
                        <PotentialValue
                          $color={POTENTIAL_STYLED_MAP[potential].color}
                        >
                          <Blur activate={hiddenData}>
                            {formatMessage({ id: getPotentialI18n(potential) })}
                          </Blur>
                        </PotentialValue>
                      ) : null
                    }
                  />
                  {reachCountEstList.map(
                    ({ titleI18nId, tooltipI18nId, formattedRange }) => (
                      <StyledKolDetailStatistic
                        key={`${titleI18nId}_${platform.shortcode}`}
                        title={formatMessage({ id: titleI18nId })}
                        tooltip={{
                          title: formatMessage({ id: tooltipI18nId }),
                        }}
                        value={
                          formattedRange ? (
                            <Blur activate={hiddenData}>{formattedRange}</Blur>
                          ) : null
                        }
                      />
                    ),
                  )}
                </>
              )}
          </StatisticsWrapper>
        )}

        <ChartWrapper>
          <KolDetailDataBlockSubHeader
            title={formatMessage({ id: 'kol:follower_analysis_chart' })}
          />
          {((): ReactNode => {
            if (isFetching) {
              return (
                <Flex
                  align='center'
                  justify='center'
                  style={{ minHeight: 140 }}
                >
                  <Spin />
                </Flex>
              )
            }

            return (
              <Spin indicator={<Blocker />} spinning={isPublicKolDetailPage}>
                <Blur activate={hiddenData}>
                  {series.some(({ y }): boolean => !!y) ? (
                    <ApexCharts
                      options={chartOptions}
                      series={[
                        {
                          name: formatMessage({
                            id: 'project:metric_followers',
                          }),
                          data: series,
                        },
                      ]}
                      type='area'
                    />
                  ) : (
                    <NoData />
                  )}
                </Blur>
              </Spin>
            )
          })()}
        </ChartWrapper>
      </Wrapper>
    </KolDetailDataBlock>
  )
}

export default FollowerAnalysis

const PotentialValue = styled.span<{ $color: string }>`
  color: ${({ $color }): string => $color};
`

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`

const StyledKolDetailStatistic = styled(KolDetailStatistic)``
const StyledPotentialKolDetailStatistic = styled(StyledKolDetailStatistic)`
  grid-column: span 2;
`

const StatisticsWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  border-right: 1px solid ${({ theme }): string => theme.colors.base.grey.grey6};

  ${StyledKolDetailStatistic} {
    flex: 1 1 150px;
  }
  ${StyledPotentialKolDetailStatistic} {
    grid-column: span 2;
  }
`

const Wrapper = styled.div<{ $widerLayoutMode: boolean }>`
  display: flex;
  gap: 30px;

  ${({ $widerLayoutMode }): FlattenSimpleInterpolation =>
    $widerLayoutMode
      ? css`
          flex-direction: row;

          @media (max-width: 980px) {
            flex-direction: column;
            border-right: none;
            ${StatisticsWrapper} {
              border-right: none;
            }
          }
        `
      : css`
          flex-direction: column;

          ${StatisticsWrapper} {
            border-right: none;
          }
        `};
`
